import React from 'react';
import { prop, assoc } from 'ramda';
import moment from 'moment';

import { Document, Page, View, Text } from '@react-pdf/renderer';

import { convertToReal } from 'helpers/converters';
import { formatServicesCategories } from 'helpers/formatters';

import { styles } from '../styles';

const col5 = { width: `${100 / 5}%` };

const getEstablishmentNameFromEstablishmentId = (establishmentId, establishments) => {
  const establishmentName = establishments
    ?.find(establishment => +establishment?.establishmentId == establishmentId)
    ?.establishmentName;

  return establishmentName || '';
}

const getEstablishmentAddressFromEstablishmentId = (establishmentId, establishments) => {
  const address = establishments
    ?.find(establishment => +establishment?.establishmentId == establishmentId)
    ?.addresses[0];

  if(!address?.number && !address?.street && !address?.district && !address?.city) {
    return '-';
  }

  if(!address?.number) {
    return `${address?.street} - ${address?.district}, ${address?.city}`;
  }

  return `${address?.street}, ${address?.number} - ${address?.district}, ${address?.city}`;
}

const getPercentage = (total, quantity) => {
  if(!total || !quantity) {
    return 0;
  }

  return `${Number(quantity * 100 / total).toFixed(2)} %`;
}

const getValidPaymentMethodsContent = content => {
  if(!Array.isArray(content)) {
    return [];
  }

  return content.filter(item => item && typeof item == 'object' && !!item?.paymentMethodName);
}

const Footer = ({ total, amount, totalPeriod, text = '', style }) => {
  const amountTotal = amount || amount == 0 ? `Total: ${convertToReal(amount)}` : '';
  const periodTotal = totalPeriod && totalPeriod !== '0' ? ` / Período total: ${moment.duration(parseInt(totalPeriod, 10), 'seconds').format('D [dia(s)], H [h e] m [min]')}` : '';

  return(
    <View style={[styles.footer, style, { display: 'flex', flexDirection: 'column', backgroundColor: '#022A5C' }]}>
      <View style={{ display: 'flex', flexDirection: 'row' }}>
        {!!total && (
          <Text style={[styles.text, { color: '#FFFFFF', marginRight: 5 }]}>
            {(+total > 1) && `Este relatório tem ${total} itens.`}
            {(+total == 1) && `Este relatório tem ${total} item.`}
          </Text>
        )}
        {!!amountTotal && (
          <Text style={[styles.text, styles.label, { color: '#FFFFFF' }]}>
            {amountTotal}
          </Text>
        )}
      </View>
      {!!periodTotal && (
        <Text style={[styles.text, { color: '#FFFFFF' }]}>
          {periodTotal}
        </Text>
      )}
      {!!text && (
        <Text style={[styles.text, styles.label, { color: '#FFFFFF' }]}>
          {text}
        </Text>
      )}
    </View>
  );
}

const CashFlow = ({ cashFlow }) => {
  return(
    <View style={{ marginTop: 15 }}>
      <View style={{ marginBottom: 10 }}>
        <Text style={[styles.fontBold, styles.fontSize15]}>
          Resumo total de todos os estabelecimentos
        </Text>
      </View>
      <View style={styles.table}>
        <View style={styles.tableRow}>
          <View style={[styles.tableColHeader, styles.cashFlowCell, styles.cashFlowSuccess]}>
            <Text style={[styles.title, styles.fontSuccess]}>
              Receita: {convertToReal(+cashFlow.entry)}
            </Text>
          </View>
          <View style={[styles.tableColHeader, styles.cashFlowCell, styles.cashFlowDanger]}>
            <Text style={[styles.title, styles.fontDanger]}>
              Despesa: {convertToReal(+cashFlow.exit)}
            </Text>
          </View>
          <View style={[styles.tableColHeader, styles.cashFlowCell, styles.cashFlowPrimary]}>
            <Text style={[styles.title, styles.fontPrimary]}>
              Lucro: {convertToReal(+cashFlow.balance)}
            </Text>
          </View>
        </View>
      </View>
    </View>
  );
}

const Resume = ({ establishments, establishmentId, data, type, isProduct = false, isDiscount = false, isServices = false }) => {
  const sumTotal = data?.content?.reduce((total, item) => total + +item?.total, 0);
  const sumAmount = data?.content?.reduce((total, item) => total + +item?.totalAmount, 0);

  return(
    <View>
      <View>
        <Text style={[styles.fontRegular, styles.fontSize13, { marginBottom: 5 }]}>
          {getEstablishmentNameFromEstablishmentId(establishmentId, establishments)}
        </Text>
        <Text style={[styles.fontRegular, styles.fontSize9, styles.fontColorGray, { marginBottom: 5 }]}>
          {getEstablishmentAddressFromEstablishmentId(establishmentId, establishments)}
        </Text>
      </View>
      <View style={styles.tableFinancialBlack}>
        <View style={styles.tableRowFinancial}>
          <View style={[styles.tableColHeaderFinancialWhite, col5]}>
            <Text style={styles.tableCellHeaderFinancialBlack}>Descrição</Text>
          </View>
          <View style={[styles.tableColHeaderFinancialWhite, col5]}>
            <Text style={styles.tableCellHeaderFinancialBlack}>Valor</Text>
          </View>
          <View style={[styles.tableColHeaderFinancialWhite, col5]}>
            <Text style={styles.tableCellHeaderFinancialBlack}>Qntd</Text>
          </View>
          <View style={[styles.tableColHeaderFinancialWhite, col5]}>
            <Text style={styles.tableCellHeaderFinancialBlack}>% Qntd</Text>
          </View>
          <View style={[styles.tableColHeaderFinancialWhite, col5]}>
            <Text style={styles.tableCellHeaderFinancialBlack}>% Fat</Text>
          </View>
        </View>
        {data?.content?.map(resume => (
          <View style={styles.tableRowFinancial}>
            <View style={[styles.tableColFinancialBlack, col5]}>
              {isProduct ? (
                <Text style={styles.tableCellFinancialBlack}>
                  {resume?.name}
                </Text>
              ) : (
                <>
                  {isServices ? (
                    <View>
                      <Text style={[styles.tableCellFinancialBlack, { marginBottom: 0 }]}>
                        {resume?.description}
                      </Text>
                      {(resume?.categories != '-') && (
                        <Text style={[styles.tableCellFinancialBlack, styles.fontSize6]}>
                          Categoria(s): {resume?.categories}
                        </Text>
                      )}
                    </View>
                  ) : (
                    <Text style={styles.tableCellFinancialBlack}>{prop(type, resume)}</Text>
                  )}
                </>
              )}
            </View>
            <View style={[styles.tableColFinancialBlack, col5]}>
              <Text style={styles.tableCellFinancialBlack}>
                {convertToReal(+resume?.totalAmount || 0)}
              </Text>
            </View>
            <View style={[styles.tableColFinancialBlack, col5]}>
              <Text style={styles.tableCellFinancialBlack}>
                {resume?.total || 0}
              </Text>
            </View>
            <View style={[styles.tableColFinancialBlack, col5]}>
              <Text style={styles.tableCellFinancialBlack}>
                {getPercentage(sumTotal, resume?.total)}
              </Text>
            </View>
            <View style={[styles.tableColFinancialBlack, col5]}>
              <Text style={styles.tableCellFinancialBlack}>
                {getPercentage(sumAmount, resume?.totalAmount)}
              </Text>
            </View>
          </View>
        ))}
        {isDiscount ? (
          <Footer
            style={{ borderStyle: 'solid 1px', borderColor: '#000000', borderWidth: 1 }}
            total={data?.total || 0}
            amount={data?.totalAmount || 0}
            text='O valor total da receita acima é resultado da soma dos serviços abaixo menos o valor total do desconto.'
          />
        ) : (
          <Footer
            style={{ borderStyle: 'solid 1px', borderColor: '#000000', borderWidth: 1 }}
            total={data?.total || 0}
            amount={data?.totalAmount || 0}
          />
        )}
      </View>
    </View>
  );
}

const Invoices = ({ establishments, establishmentId, data }) => {
  const { paidOut } = data?.content || {};
  const invoices = [assoc('label', 'Pagas', paidOut)];

  const sumTotal = invoices?.reduce((total, item) => total + +item?.total, 0);
  const sumAmount = invoices?.reduce((total, item) => total + +item?.totalAmount, 0);

  return(
    <View>
      <View>
        <Text style={[styles.fontRegular, styles.fontSize13, { marginBottom: 5 }]}>
          {getEstablishmentNameFromEstablishmentId(establishmentId, establishments)}
        </Text>
        <Text style={[styles.fontRegular, styles.fontSize9, styles.fontColorGray, { marginBottom: 5 }]}>
          {getEstablishmentAddressFromEstablishmentId(establishmentId, establishments)}
        </Text>
      </View>
      <View style={styles.tableFinancialBlack}>
        <View style={styles.tableRowFinancial}>
          <View style={[styles.tableColHeaderFinancialWhite, col5]}>
            <Text style={styles.tableCellHeaderFinancialBlack}>Descrição</Text>
          </View>
          <View style={[styles.tableColHeaderFinancialWhite, col5]}>
            <Text style={styles.tableCellHeaderFinancialBlack}>Valor</Text>
          </View>
          <View style={[styles.tableColHeaderFinancialWhite, col5]}>
            <Text style={styles.tableCellHeaderFinancialBlack}>Qntd</Text>
          </View>
          <View style={[styles.tableColHeaderFinancialWhite, col5]}>
            <Text style={styles.tableCellHeaderFinancialBlack}>% Qntd</Text>
          </View>
          <View style={[styles.tableColHeaderFinancialWhite, col5]}>
            <Text style={styles.tableCellHeaderFinancialBlack}>% Fat</Text>
          </View>
        </View>
        {invoices.map(invoice => invoice.totalAmount != 0 && (
          <View style={styles.tableRowFinancial}>
            <View style={[styles.tableColFinancialBlack, col5]}>
              <Text style={styles.tableCellFinancialBlack}>
                {invoice.label}
              </Text>
            </View>
            <View style={[styles.tableColFinancialBlack, col5]}>
              <Text style={styles.tableCellFinancialBlack}>
                {convertToReal(+invoice.totalAmount || 0)}
              </Text>
            </View>
            <View style={[styles.tableColFinancialBlack, col5]}>
              <Text style={styles.tableCellFinancialBlack}>
                {invoice.total || 0}
              </Text>
            </View>
            <View style={[styles.tableColFinancialBlack, col5]}>
              <Text style={styles.tableCellFinancialBlack}>
                {getPercentage(sumTotal, invoice?.total)}
              </Text>
            </View>
            <View style={[styles.tableColFinancialBlack, col5]}>
              <Text style={styles.tableCellFinancialBlack}>
                {getPercentage(sumAmount, invoice?.totalAmount)}
              </Text>
            </View>
          </View>
        ))}
        <Footer
          style={{ borderStyle: 'solid 1px', borderColor: '#000000', borderWidth: 1 }}
          total={data?.total || 0}
          amount={data?.totalAmount || 0}
        />
      </View>
    </View>
  );
}

const PaymentMethods = ({ isAllEstablishments = false, establishments, establishmentId, data }) => {
  const paymentMethodsContent = getValidPaymentMethodsContent(data?.content);
  const sumQuantity = paymentMethodsContent?.reduce((total, item) => total + +item?.total, 0);
  const sumAmount = paymentMethodsContent?.reduce((total, item) => total + +item?.totalAmount, 0);

  return(
    <View>
      {(establishments?.length > 0 && establishmentId) && (
        <View>
          <Text style={[styles.fontRegular, styles.fontSize13, { marginBottom: 5 }]}>
            {getEstablishmentNameFromEstablishmentId(establishmentId, establishments)}
          </Text>
          <Text style={[styles.fontRegular, styles.fontSize9, styles.fontColorGray, { marginBottom: 5 }]}>
            {getEstablishmentAddressFromEstablishmentId(establishmentId, establishments)}
          </Text>
        </View>
      )}
      <View style={styles.tableFinancialBlack}>
        <View style={styles.tableRowFinancial}>
          <View style={[styles.tableColHeaderFinancialWhite, col5]}>
            <Text style={styles.tableCellHeaderFinancialBlack}>
              Descrição
            </Text>
          </View>
          <View style={[styles.tableColHeaderFinancialWhite, col5]}>
            <Text style={styles.tableCellHeaderFinancialBlack}>
              Valor
            </Text>
          </View>
          <View style={[styles.tableColHeaderFinancialWhite, col5]}>
            <Text style={styles.tableCellHeaderFinancialBlack}>
              {isAllEstablishments ? 'Quantidade' : 'Qntd'}
            </Text>
          </View>
          <View style={[styles.tableColHeaderFinancialWhite, col5]}>
            <Text style={styles.tableCellHeaderFinancialBlack}>
              {isAllEstablishments ? '% Quantitativo' : '% Qntd'}
            </Text>
          </View>
          <View style={[styles.tableColHeaderFinancialWhite, col5]}>
            <Text style={styles.tableCellHeaderFinancialBlack}>
              {isAllEstablishments ? '% Faturamento' : '% Fat'}
            </Text>
          </View>
        </View>
        {paymentMethodsContent?.map(item => {
          return(
            <View style={styles.tableRowFinancial}>
              <View style={[styles.tableColFinancialBlack, col5]}>
                <Text style={styles.tableCellFinancialBlack}>
                  {item?.paymentMethodName || ''}
                </Text>
              </View>
              <View style={[styles.tableColFinancialBlack, col5]}>
                <Text style={styles.tableCellFinancialBlack}>
                  {convertToReal(item?.totalAmount || 0)}
                </Text>
              </View>
              <View style={[styles.tableColFinancialBlack, col5]}>
                <Text style={styles.tableCellFinancialBlack}>
                  {item?.total || 0}
                </Text>
              </View>
              <View style={[styles.tableColFinancialBlack, col5]}>
                <Text style={styles.tableCellFinancialBlack}>
                  {getPercentage(sumQuantity, item?.total)}
                </Text>
              </View>
              <View style={[styles.tableColFinancialBlack, col5]}>
                <Text style={styles.tableCellFinancialBlack}>
                  {getPercentage(sumAmount, item?.totalAmount)}
                </Text>
              </View>
            </View>
          );
        })}
        <Footer
          style={{ borderStyle: 'solid 1px', borderColor: '#000000', borderWidth: 1 }}
          total={data?.total || 0}
          amount={data?.totalAmount || 0}
        />
      </View>
    </View>
  );
}

const FinancialResumeAllEstablishments = ({ exportData, period, servicesCategories, allEstablishments }) => {
  const formattedCashflow = {
    exit: exportData?.reduce((total, item) => +item?.cashFlow?.exit + +total, 0),
    entry: exportData?.reduce((total, item) => +item?.cashFlow?.entry + +total, 0),
    balance: exportData?.reduce((total, item) => +item?.cashFlow?.balance + +total, 0)
  }

  const paymentMethods = exportData
    ?.map(establishment => getValidPaymentMethodsContent(establishment?.paymentMethods?.content))
    ?.filter(paymentMethod => paymentMethod?.length > 0)
    ?.flat()
    ?.reduce((accumulator, item) => {
      if(!item?.paymentMethodName) {
        return accumulator;
      }

      const key = item?.paymentMethodName;

      if(accumulator[key]) {
        accumulator[key] = {
          paymentMethodName: item?.paymentMethodName,
          total: +accumulator[key]?.total + +item?.total,
          totalAmount: +accumulator[key]?.totalAmount + +item?.totalAmount
        }
      }

      if(!accumulator[key]) {
        accumulator[key] = {
          paymentMethodName: item?.paymentMethodName,
          total: +item?.total,
          totalAmount: +item?.totalAmount
        }
      }

      return accumulator;
    }, {}) || {};

  delete paymentMethods['total'];
  delete paymentMethods['totalAmount'];
  delete paymentMethods['paymentMethodName'];
  delete paymentMethods['establishmentPaymentMethodId'];

  const allEstablishmentsPaymentMethods = Object
    ?.keys(paymentMethods)
    ?.map(key => {
      return paymentMethods[key];
    });

  const allEstablishmentsPaymentMethodsTotal = {
    content: allEstablishmentsPaymentMethods,
    total: allEstablishmentsPaymentMethods?.reduce((total, item) => +item?.total + +total, 0),
    totalAmount: allEstablishmentsPaymentMethods?.reduce((total, item) => +item?.totalAmount + +total, 0)
  }

  const discountsTotal = {
    total: exportData?.reduce((total, item) => +item?.discounts?.total + +total, 0),
    totalAmount: exportData?.reduce((total, item) => +item?.discounts?.totalAmount + +total, 0)
  }

  const serviceOrdersTotal = {
    total: exportData?.reduce((total, item) => +item?.serviceOrders?.total + +total, 0),
    totalAmount: exportData?.reduce((total, item) => +item?.serviceOrders?.totalAmount + +total, 0)
  }

  const servicesTotal = {
    total: exportData?.reduce((total, item) => +item?.services?.total + +total, 0),
    totalAmount: exportData?.reduce((total, item) => +item?.services?.totalAmount + +total, 0)
  }

  const productsTotal = {
    total: exportData?.reduce((total, item) => +item?.products?.total + +total, 0),
    totalAmount: exportData?.reduce((total, item) => +item?.products?.totalAmount + +total, 0)
  }

  const productsOsTotal = {
    total: exportData?.reduce((total, item) => +item?.productsOs?.total || 0 + +total, 0),
    totalAmount: exportData?.reduce((total, item) => +item?.productsOs?.totalAmount || 0 + +total, 0)
  }

  const entryCategoriesTotal = {
    total: exportData?.reduce((total, item) => +item?.entryCategory?.total + +total, 0),
    totalAmount: exportData?.reduce((total, item) => +item?.entryCategory?.totalAmount + +total, 0)
  }

  const exitCategoriesTotal = {
    total: exportData?.reduce((total, item) => +item?.exitCategory?.total + +total, 0),
    totalAmount: exportData?.reduce((total, item) => +item?.exitCategory?.totalAmount + +total, 0)
  }

  const invoices = {
    total: exportData?.reduce((total, item) => +item?.invoices?.total + +total, 0),
    totalAmount: exportData?.reduce((total, item) => +item?.invoices?.totalAmount + +total, 0)
  }

  return(
    <Document>
      <Page style={{ padding: 5 }}>
        <View>
          <Text style={[styles.fontBold, styles.fontSize15]}>
            Resumo do faturamento total de todos os estabelecimentos
          </Text>
          <View style={{ display: 'flex', flexDirection: 'row' }}>
            <Text style={[styles.fontBold, styles.fontSize15, { marginRight: 5 }]}>
              Período:
            </Text>
            <Text style={[styles.fontRegular, styles.fontSize15]}>
              {period?.toLowerCase()?.replace('resumo financeiro', '')?.trim()}
            </Text>
          </View>
        </View>
        <CashFlow cashFlow={formattedCashflow} />
        <View style={{ width: '100%', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', marginTop: 10 }}>
          <View style={{ width: '100%' }}>
            <Text style={[styles.fontBold, styles.fontSize13, { marginBottom: 10 }]}>
              MEIOS DE PAGAMENTO DE TODOS OS ESTABELECIMENTOS
            </Text>
          </View>
          <View wrap={false}>
            <PaymentMethods
              isAllEstablishments
              data={allEstablishmentsPaymentMethodsTotal}
            />
          </View>
        </View>
        <View style={{ width: '100%', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', marginTop: 10 }}>
          <View style={{ width: '100%' }}>
            <Text style={[styles.fontBold, styles.fontSize13, { marginBottom: 10 }]}>
              MEIOS DE PAGAMENTO POR ESTABELECIMENTO
            </Text>
          </View>
          {exportData?.map(item => {
            const defaultPaymentMethods = [
              { paymentMethodName: '', total: 0, totalAmount: 0 }
            ];

            const paymentMethodsContent = getValidPaymentMethodsContent(item?.paymentMethods?.content);

            return(
              <View wrap={false} style={{ width: '49%' }}>
                <PaymentMethods
                  establishments={allEstablishments}
                  establishmentId={item?.establishmentId}
                  data={
                    paymentMethodsContent?.length > 0
                      ? { ...item?.paymentMethods, content: paymentMethodsContent }
                      : { ...item?.paymentMethods, content: defaultPaymentMethods }
                  }
                />
              </View>
            );
          })}
        </View>
      </Page>
      {(+discountsTotal.total > 0 || +discountsTotal.totalAmount > 0) && (
        <Page style={{ padding: 5 }}>
          <View style={{ width: '100%', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', marginTop: 10 }}>
            <View style={{ width: '100%' }}>
              <Footer
                style={{ borderStyle: 'solid 1px', borderColor: '#000000', borderWidth: 1 }}
                total={discountsTotal.total || 0}
                amount={discountsTotal.totalAmount || 0}
              />
              <Text style={[styles.fontBold, styles.fontSize13, { marginTop: 10, marginBottom: 10 }]}>
                DESCONTOS
              </Text>
            </View>
            {exportData?.map(item => {
              return(
                <View wrap={false} style={{ width: '49%' }}>
                  <Resume
                    isDiscount
                    establishments={allEstablishments}
                    establishmentId={item?.establishmentId}
                    type="description"
                    data={item?.discounts}
                  />
                </View>
              );
            })}
          </View>
        </Page>
      )}
      {(+serviceOrdersTotal.total > 0 || +serviceOrdersTotal.totalAmount > 0) && (
        <Page style={{ padding: 5 }}>
          <View style={{ width: '100%', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', marginTop: 10 }}>
            <View style={{ width: '100%' }}>
              <Footer
                style={{ borderStyle: 'solid 1px', borderColor: '#000000', borderWidth: 1 }}
                total={serviceOrdersTotal.total || 0}
                amount={serviceOrdersTotal.totalAmount || 0}
              />
              <Text style={[styles.fontBold, styles.fontSize13, { marginTop: 10, marginBottom: 10 }]}>
                ROTATIVOS
              </Text>
            </View>
            {exportData?.map(item => {
              const defaultTypePrice = [
                { typePrice: '', total: 0, totalAmount: 0 }
              ];

              return(
                <View wrap={false} style={{ width: '49%' }}>
                  <Resume
                    establishments={allEstablishments}
                    establishmentId={item?.establishmentId}
                    type="typePrice"
                    data={
                      item?.serviceOrders?.content?.length > 0
                        ? item?.serviceOrders
                        : { ...item?.serviceOrders, content: defaultTypePrice }
                    }
                  />
                </View>
              );
            })}
          </View>
        </Page>
      )}
      {(+servicesTotal.total > 0 || +servicesTotal.totalAmount > 0) && (
        <Page style={{ padding: 5 }}>
          <View style={{ width: '100%', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', marginTop: 10 }}>
            <View style={{ width: '100%' }}>
              <Footer
                style={{ borderStyle: 'solid 1px', borderColor: '#000000', borderWidth: 1 }}
                total={servicesTotal.total || 0}
                amount={servicesTotal.totalAmount || 0}
              />
              <Text style={[styles.fontBold, styles.fontSize13, { marginTop: 10, marginBottom: 10 }]}>
                SERVIÇOS
              </Text>
            </View>
            {exportData?.map(item => {
              return(
                <View wrap={false} style={{ width: '49%' }}>
                  <Resume
                    isServices
                    establishments={allEstablishments}
                    establishmentId={item?.establishmentId}
                    type="description"
                    data={{
                      ...item?.services,
                      content: item?.services?.content?.length > 0
                        ? item?.services?.content?.map(service => {
                          return {
                            ...service,
                            categories: formatServicesCategories(service?.serviceCategories, servicesCategories)
                          }
                        })
                        : [
                          {
                            description: '',
                            categories: '',
                            total: 0,
                            totalAmount: 0
                          }
                        ]
                    }}
                  />
                </View>
              );
            })}
          </View>
        </Page>
      )}
      {(+productsTotal.total > 0 || +productsTotal.totalAmount > 0) && (
        <Page style={{ padding: 5 }}>
          <View style={{ width: '100%', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', marginTop: 10 }}>
            <View style={{ width: '100%' }}>
              <Footer
                style={{ borderStyle: 'solid 1px', borderColor: '#000000', borderWidth: 1 }}
                total={productsTotal.total || 0}
                amount={productsTotal.totalAmount || 0}
              />
              <Text style={[styles.fontBold, styles.fontSize13, { marginTop: 10, marginBottom: 10 }]}>
                PRODUTOS
              </Text>
            </View>
            {exportData?.map(item => {
              const defaultProducts = [
                { name: '', total: 0, totalAmount: 0 }
              ];

              return(
                <View wrap={false} style={{ width: '49%' }}>
                  <Resume
                    isProduct
                    establishments={allEstablishments}
                    establishmentId={item?.establishmentId}
                    type="description"
                    data={
                      item?.products?.content?.length > 0
                        ? item?.products
                        : { ...item?.products, content: defaultProducts }
                    }
                  />
                </View>
              );
            })}
          </View>
        </Page>
      )}
      {(+productsOsTotal.total > 0 || +productsOsTotal.totalAmount > 0) && (
        <Page style={{ padding: 5 }}>
          <View style={{ width: '100%', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', marginTop: 10 }}>
            <View style={{ width: '100%' }}>
              <Footer
                style={{ borderStyle: 'solid 1px', borderColor: '#000000', borderWidth: 1 }}
                total={productsOsTotal.total || 0}
                amount={productsOsTotal.totalAmount || 0}
              />
              <Text style={[styles.fontBold, styles.fontSize13, { marginTop: 10, marginBottom: 10 }]}>
                PRODUTOS (OS)
              </Text>
            </View>
            {exportData?.map(item => {
              const defaultProducts = [
                { name: '', total: 0, totalAmount: 0 }
              ];

              return(
                <View wrap={false} style={{ width: '49%' }}>
                  <Resume
                    isProduct
                    establishments={allEstablishments}
                    establishmentId={item?.establishmentId}
                    type="description"
                    data={
                      item?.productsOs?.content?.length > 0
                        ? item?.productsOs
                        : { ...item?.productsOs, content: defaultProducts }
                    }
                  />
                </View>
              );
            })}
          </View>
        </Page>
      )}
      {(+entryCategoriesTotal.total > 0 || +entryCategoriesTotal.totalAmount > 0) && (
        <Page style={{ padding: 5 }}>
          <View style={{ width: '100%', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', marginTop: 10 }}>
            <View style={{ width: '100%' }}>
              <Footer
                style={{ borderStyle: 'solid 1px', borderColor: '#000000', borderWidth: 1 }}
                total={entryCategoriesTotal.total || 0}
                amount={entryCategoriesTotal.totalAmount || 0}
              />
              <Text style={[styles.fontBold, styles.fontSize13, { marginTop: 10, marginBottom: 10 }]}>
                MOVIMENTAÇÕES DE ENTRADA
              </Text>
            </View>
            {exportData?.map(item => {
              const defaultEntryCategories = [
                { categoryName: '', total: 0, totalAmount: 0 }
              ];

              return(
                <View wrap={false} style={{ width: '49%' }}>
                  <Resume
                    establishments={allEstablishments}
                    establishmentId={item?.establishmentId}
                    type="categoryName"
                    data={
                      item?.entryCategory?.content?.length > 0
                        ? item?.entryCategory
                        : { ...item?.entryCategory, content: defaultEntryCategories }
                    }
                  />
                </View>
              );
            })}
          </View>
        </Page>
      )}
      {(+exitCategoriesTotal.total > 0 || +exitCategoriesTotal.totalAmount > 0) && (
        <Page style={{ padding: 5 }}>
          <View style={{ width: '100%', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', marginTop: 10 }}>
            <View style={{ width: '100%' }}>
              <Footer
                style={{ borderStyle: 'solid 1px', borderColor: '#000000', borderWidth: 1 }}
                total={exitCategoriesTotal.total || 0}
                amount={exitCategoriesTotal.totalAmount || 0}
              />
              <Text style={[styles.fontBold, styles.fontSize13, { marginTop: 10, marginBottom: 10 }]}>
                MOVIMENTAÇÕES DE SAÍDA
              </Text>
            </View>
            {exportData?.map(item => {
              const defaultExitCategories = [
                { categoryName: '', total: 0, totalAmount: 0 }
              ];

              return(
                <View wrap={false} style={{ width: '49%' }}>
                  <Resume
                    establishments={allEstablishments}
                    establishmentId={item?.establishmentId}
                    type="categoryName"
                    data={
                      item?.exitCategory?.content?.length > 0
                        ? item?.exitCategory
                        : { ...item?.exitCategory, content: defaultExitCategories }
                    }
                  />
                </View>
              );
            })}
          </View>
        </Page>
      )}
      {(+invoices.total > 0 || +invoices.totalAmount > 0) && (
        <Page style={{ padding: 5 }}>
          <View style={{ width: '100%', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', marginTop: 10 }}>
            <View style={{ width: '100%' }}>
              <Footer
                style={{ borderStyle: 'solid 1px', borderColor: '#000000', borderWidth: 1 }}
                total={invoices.total || 0}
                amount={invoices.totalAmount || 0}
              />
              <Text style={[styles.fontBold, styles.fontSize13, { marginTop: 10, marginBottom: 10 }]}>
                FATURAS
              </Text>
            </View>
            {exportData?.map(item => {
              const defaultInvoice = {
                paidOut: { total: 0, totalAomunt: 0 }
              }

              return(
                <View wrap={false} style={{ width: '49%' }}>
                  <Invoices
                    establishments={allEstablishments}
                    establishmentId={item?.establishmentId}
                    data={
                      (+item?.invoices?.content?.paidOut?.total > 0 || +item?.invoices?.content?.paidOut?.totalAmount > 0)
                        ? item?.invoices
                        : { ...item?.invoices, content: defaultInvoice }
                    }
                  />
                </View>
              );
            })}
          </View>
        </Page>
      )}
    </Document>
  )
}

export default FinancialResumeAllEstablishments;